// CondensadoPapel.tsx
// Fechamento de Ciclo, aba "4 · Condensado TDAH-friendly": o Condensado (Amargosa) no modo
// "passar para o papel". Desenho aprovado pelo Walber no Claude Design em 02/10/2026.
// SÓ LEITURA E SÓ TELA: não chama o motor e não grava nada no servidor. Recebe os MESMOS textos que a
// aba "3 · Condensado (Amargosa)" mostra, já formatados pelo FechamentoCiclo.tsx, e só muda a forma
// de apresentar. A única conta daqui é a conferência final (soma das áreas x TOTAL), que só compara:
// não muda nenhum número mostrado.
// Regras do papel (foto do formulário, 02/10/2026): as 22 colunas vão na ordem do papel, área por área,
// em 4 blocos (colunas 1-5, 6-12, 13-16 e 17-22). No cabeçalho só se escreve o Município, o último
// algarismo do ano e "Condensado dos Dados" no canto; a Atividade e a palavra TOTAL (última linha do
// formulário) já vêm impressas, por isso o TOTAL não tem o bloco de identificação.
// TOTAL (07/10/2026, pedido do Walber): no lugar da identificação, o TOTAL abre com o bloco "Datas do ciclo"
// (INIC e TERM, colunas 4 e 5), que também se escreve no papel. Sem as datas, o TOTAL fica com 3 blocos.
// O progresso (blocos copiados e bloco atual) fica guardado só neste navegador, por ciclo (chave
// "tovis-condensado-papel-<ciclo>"), junto com os números que foram copiados: se um número mudar
// depois, o bloco perde a marca e aparece como "Mudou".
// VERMELHO (07/10/2026, pedido do Walber): positivos e índices aparecem em vermelho, como ele escreve no papel.
// NÃO usar a classe .fc-folha (é a que o botão Arquivar do Condensado copia) nem CSS de impressão próprio.
import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface SituacaoAreaPapel {
  area: string;
  semRG: boolean;
  fechada: boolean;
  positivosPapel: boolean;
  linhaPapel: boolean;
}

interface Props {
  titulos: string[];
  linhas: string[][];
  total: string[];
  municipio: string;
  atividade: string;
  ano: number | string;
  nomeCiclo: string;
  cicloId: number | string | null | undefined;
  situacoes: SituacaoAreaPapel[];
  emAndamento: string[];
  bloqueios: { area: string; motivo: string }[];
}

const N_COLUNAS = 22;
const VERSAO_SALVO = 1;

interface Bloco {
  nome: string;
  de: number;
  ate: number;
  cor: string;
  span: string;
  pesos: number[];
  lupa: number[];
}

// Os 4 blocos de cada linha do papel (colunas contadas a partir de 0)
const BLOCOS: Bloco[] = [
  { nome: 'Identificação', de: 0, ate: 4, cor: '#475569', span: 'span 9', pesos: [2.2, 1, 1, 1, 1], lupa: [2, 1, 1, 1, 1] },
  { nome: 'Imóveis', de: 5, ate: 11, cor: '#1D4ED8', span: 'span 11', pesos: [1, 1, 1, 1, 1, 1, 1], lupa: [1, 1, 1, 1, 1, 1, 1] },
  { nome: 'Depósitos', de: 12, ate: 15, cor: '#0E7490', span: 'span 8', pesos: [1, 1, 1, 1], lupa: [1, 1, 1, 1] },
  { nome: 'Índices, produção e larvicida', de: 16, ate: 21, cor: '#6D28D9', span: 'span 12', pesos: [1, 1.1, 1, 1.1, 1.3, 1.5], lupa: [1, 1, 1, 1, 2, 1] },
];

// Colunas escritas em vermelho no papel (contadas a partir de 0): IMOV POS, DEP POS, IIP e IND PEND.
// REND H/DIA e BTI por imóvel vão em preto (07/10/2026, pedido do Walber).
const COLUNAS_VERMELHAS = [11, 15, 16, 17];

// Colunas de contagem (somáveis) usadas só na conferência final: soma das áreas x TOTAL
const COLUNAS_SOMAVEIS = [5, 6, 7, 8, 10, 11, 12, 13, 14, 15, 18, 20];

const COR = {
  fundo: '#EEF2F7',
  marinho: '#0F2347',
  azul: '#1D4ED8',
  azulClaro: '#DBEAFE',
  texto: '#0F172A',
  cinza: '#475569',
  cinzaClaro: '#64748B',
  borda: '#D5DEEA',
  verde: '#166534',
  verdeFundo: '#DCFCE7',
  ambar: '#854D0E',
  ambarFundo: '#FEF3C7',
  vermelho: '#991B1B',
  vermelhoFundo: '#FEE2E2',
  marca: '#0E7490',
  caneta: '#B91C1C',
  canetaClara: '#B45454',
};

interface Celula {
  rot: string;
  n: string;
  v: string;
  peso: number;
  lupa: number;
  texto: boolean;
  vermelho?: boolean;
}

export interface Passo {
  id: string;
  cartao: number;
  tipo: 'cab' | 'bloco';
  area: string;
  nome: string;
  cor: string;
  span: string;
  faixa: string;
  faixaLonga: string;
  celulas: Celula[];
  extra: string;
  onde: string;
  assinatura: string;
}

interface DadosPapel {
  titulos: string[];
  linhas: string[][];
  total: string[];
  municipio: string;
  atividade: string;
  ano: number | string;
  situacoes: SituacaoAreaPapel[];
}

function textoSituacao(s: SituacaoAreaPapel | undefined): string {
  if (!s) return 'Sem informação da situação desta área no Painel do Ciclo.';
  const partes: string[] = [];
  if (s.fechada) partes.push(s.semRG ? 'Área fechada (sem RG cadastrado)' : 'Área fechada');
  else partes.push(s.semRG ? 'Falta cadastrar o RG da área' : 'Área aberta no Fechamento de Área');
  if (s.linhaPapel) partes.push('Linha registrada do papel');
  partes.push(s.positivosPapel ? 'Positivos do papel' : 'Positivos do sistema');
  return partes.join(' · ');
}

// Monta a fila de passos: cabeçalho, 4 blocos por área e os blocos do TOTAL (datas do ciclo + 3), na ordem em que se escreve no papel
export function montarPassos(d: DadosPapel): Passo[] {
  const passos: Passo[] = [];
  const anoTxt = String(d.ano);
  const celCab: Celula[] = [
    { rot: 'MUNICÍPIO', n: '', v: d.municipio, peso: 1.2, lupa: 2, texto: true },
    { rot: `ANO: COMPLETAR O ${anoTxt.slice(0, -1)}_`, n: '', v: anoTxt.slice(-1), peso: 1, lupa: 2, texto: false },
    { rot: 'CANTO SUPERIOR DIREITO', n: '', v: 'Condensado dos Dados', peso: 2, lupa: 3, texto: true },
  ];
  passos.push({
    id: 'cab',
    cartao: 0,
    tipo: 'cab',
    area: 'Cabeçalho do formulário',
    nome: 'O que se escreve à mão',
    cor: COR.cinza,
    span: 'span 12',
    faixa: 'fora da tabela',
    faixaLonga: 'fora da tabela',
    celulas: celCab,
    extra: `A atividade (${d.atividade}) já vem impressa no formulário: não precisa escrever.`,
    onde: 'Você está aqui · Topo do formulário',
    assinatura: celCab.map((c) => c.v).join('|'),
  });

  const nAreas = d.linhas.length;
  const todas = [...d.linhas, d.total];
  const temDatasTotal = (d.total[3] ?? '') !== '' || (d.total[4] ?? '') !== '';
  todas.forEach((linha, r) => {
    const ehTotal = r === nAreas;
    const area = linha[0] || (ehTotal ? 'TOTAL' : `Linha ${r + 1}`);
    const extra = ehTotal
      ? 'A palavra TOTAL já vem impressa na última linha do formulário · Contagens somadas das áreas · Índices calculados sobre os totais, com casas decimais cortadas'
      : textoSituacao(d.situacoes.find((s) => s.area === area));
    BLOCOS.forEach((b, k) => {
      // No TOTAL, o 1º bloco são só as datas do ciclo (INIC e TERM); sem datas, o TOTAL não tem esse bloco
      const datas = ehTotal && k === 0;
      if (datas && !temDatasTotal) return;
      const de = datas ? 3 : b.de;
      const celulas: Celula[] = [];
      for (let i = de; i <= b.ate; i++) {
        celulas.push({ rot: d.titulos[i], n: String(i + 1), v: linha[i] ?? '', peso: b.pesos[i - b.de], lupa: b.lupa[i - b.de], texto: i === 0, vermelho: COLUNAS_VERMELHAS.indexOf(i) >= 0 });
      }
      passos.push({
        id: ehTotal ? `t:${k}` : `a:${area}:${k}`,
        cartao: r + 1,
        tipo: 'bloco',
        area,
        nome: datas ? 'Datas do ciclo' : b.nome,
        cor: b.cor,
        span: datas ? '5 / span 5' : ehTotal && k === 1 && !temDatasTotal ? '10 / span 11' : b.span,
        faixa: `col. ${de + 1}–${b.ate + 1}`,
        faixaLonga: `colunas ${de + 1} a ${b.ate + 1} do papel`,
        celulas,
        extra,
        onde: ehTotal
          ? `Você está aqui · Última linha do formulário · Bloco ${temDatasTotal ? k + 1 : k} de ${temDatasTotal ? BLOCOS.length : BLOCOS.length - 1}`
          : `Você está aqui · Linha ${r + 1} de ${nAreas} · Bloco ${k + 1} de ${BLOCOS.length}`,
        assinatura: celulas.map((c) => c.v).join('|'),
      });
    });
  });
  return passos;
}

// Conferência final: a soma das áreas bate com o TOTAL? (null = não dá para conferir)
export function conferirTotal(linhas: string[][], total: string[]): boolean | null {
  const num = (s: string | undefined): number => (s && /^-?\d+([.,]\d+)?$/.test(s.trim()) ? parseFloat(s.trim().replace(',', '.')) : NaN);
  let confere = true;
  for (const i of COLUNAS_SOMAVEIS) {
    const t = num(total[i]);
    if (isNaN(t)) return null;
    let soma = 0;
    for (const l of linhas) {
      const v = num(l[i]);
      if (isNaN(v)) return null;
      soma += v;
    }
    if (Math.abs(soma - t) > 0.05) confere = false;
  }
  return confere;
}

interface Salvo {
  copiadas: Record<string, string>;
  cursor: string | null;
}

function lerSalvo(chave: string): Salvo {
  const vazio: Salvo = { copiadas: {}, cursor: null };
  try {
    const texto = window.localStorage.getItem(chave);
    if (!texto) return vazio;
    const o = JSON.parse(texto);
    if (!o || o.v !== VERSAO_SALVO || typeof o.copiadas !== 'object' || o.copiadas === null) return vazio;
    const copiadas: Record<string, string> = {};
    Object.keys(o.copiadas).forEach((id) => {
      if (typeof o.copiadas[id] === 'string') copiadas[id] = o.copiadas[id];
    });
    return { copiadas, cursor: typeof o.cursor === 'string' ? o.cursor : null };
  } catch {
    return vazio;
  }
}

type Estado = 'pendente' | 'copiado' | 'mudou';

const TECLA: React.CSSProperties = {
  display: 'inline-flex',
  minWidth: 24,
  justifyContent: 'center',
  padding: '2px 7px',
  border: '1px solid #CBD5E1',
  borderBottomWidth: 2,
  borderRadius: 6,
  background: '#F8FAFC',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 12,
  color: COR.texto,
};
const DICA: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 5 };
const PILULA: React.CSSProperties = { padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' };

export default function CondensadoPapel({
  titulos, linhas, total, municipio, atividade, ano, nomeCiclo, cicloId, situacoes, emAndamento, bloqueios,
}: Props) {
  const chave = `tovis-condensado-papel-${cicloId || 'sem-ciclo'}`;
  const [copiadas, setCopiadas] = useState<Record<string, string>>({});
  const [cursor, setCursor] = useState<string | null>(null);
  const [carregado, setCarregado] = useState<string | null>(null);
  const rolar = useRef(false);
  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  // Carrega o progresso guardado deste ciclo (só neste navegador)
  useEffect(() => {
    const s = lerSalvo(chave);
    setCopiadas(s.copiadas);
    setCursor(s.cursor);
    setCarregado(chave);
  }, [chave]);

  // Guarda o progresso a cada mudança
  useEffect(() => {
    if (carregado !== chave) return;
    try {
      window.localStorage.setItem(chave, JSON.stringify({ v: VERSAO_SALVO, copiadas, cursor }));
    } catch {
      /* sem armazenamento: as marcas valem só enquanto a tela estiver aberta */
    }
  }, [carregado, chave, copiadas, cursor]);

  const formatoOk = titulos.length === N_COLUNAS && total.length === N_COLUNAS && linhas.every((l) => l.length === N_COLUNAS);

  const passos = useMemo(
    () => (formatoOk ? montarPassos({ titulos, linhas, total, municipio, atividade, ano, situacoes }) : []),
    [formatoOk, titulos, linhas, total, municipio, atividade, ano, situacoes],
  );

  const estado = (p: Passo): Estado => {
    const salvo = copiadas[p.id];
    if (salvo === undefined) return 'pendente';
    return salvo === p.assinatura ? 'copiado' : 'mudou';
  };

  let idxCur = passos.findIndex((p) => p.id === cursor);
  if (idxCur < 0) idxCur = passos.findIndex((p) => estado(p) !== 'copiado');
  if (idxCur < 0) idxCur = passos.length - 1;
  const cur: Passo | undefined = passos[idxCur];
  const curId = cur ? cur.id : null;

  // Ao andar pelo teclado: o bloco atual rola para o meio da tela e recebe o foco
  useEffect(() => {
    if (!rolar.current || !curId) return;
    rolar.current = false;
    const el = refs.current[curId];
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const botao = el.querySelector('[data-cdp-ir]') as HTMLButtonElement | null;
      if (botao) botao.focus({ preventScroll: true });
    }
  }, [curId]);

  if (!formatoOk) {
    return (
      <div className="fc-noprint" style={{ padding: 16, borderRadius: 12, background: COR.vermelhoFundo, color: COR.vermelho, fontWeight: 600 }}>
        O Condensado veio em um formato diferente do esperado ({N_COLUNAS} colunas). Use a aba "3 · Condensado (Amargosa)" e avise o suporte.
      </div>
    );
  }
  if (!cur) return null;

  const irPara = (id: string, peloTeclado: boolean) => {
    rolar.current = peloTeclado;
    setCursor(id);
  };
  const marcar = (p: Passo) => setCopiadas((c) => ({ ...c, [p.id]: p.assinatura }));
  const desmarcar = (p: Passo) =>
    setCopiadas((c) => {
      const n = { ...c };
      delete n[p.id];
      return n;
    });

  const aoTeclar = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const k = e.key;
    const espaco = k === ' ' || k === 'Spacebar';
    if (!(k === 'ArrowDown' || k === 'ArrowUp' || espaco || k === 'Enter' || k === 'Backspace')) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    e.preventDefault();
    if (k === 'ArrowDown') {
      if (idxCur < passos.length - 1) irPara(passos[idxCur + 1].id, true);
      return;
    }
    if (k === 'ArrowUp') {
      if (idxCur > 0) irPara(passos[idxCur - 1].id, true);
      return;
    }
    if (k === 'Backspace') {
      if (idxCur > 0) {
        desmarcar(passos[idxCur - 1]);
        irPara(passos[idxCur - 1].id, true);
      }
      return;
    }
    marcar(cur);
    if (idxCur < passos.length - 1) irPara(passos[idxCur + 1].id, true);
  };
  const aoSoltarTecla = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') e.preventDefault();
  };

  const recomecar = () => {
    if (!window.confirm('Apagar todas as marcas de "copiei" deste ciclo e voltar ao começo?')) return;
    setCopiadas({});
    irPara(passos[0].id, false);
  };

  const nCopiados = passos.filter((p) => estado(p) === 'copiado').length;
  const pct = `${Math.round((nCopiados / passos.length) * 100)}%`;
  const mudaram = passos.filter((p) => estado(p) === 'mudou');

  const sitDe = (area: string) => situacoes.find((s) => s.area === area);
  const semRG = emAndamento.filter((a) => sitDe(a)?.semRG);
  const abertas = emAndamento.filter((a) => !sitDe(a)?.semRG);

  const prox = passos[idxCur + 1];
  const proxTxt = prox ? `Próximo: ${prox.area === cur.area ? prox.nome : `${prox.area} · ${prox.nome}`}` : 'Último bloco';
  const estadoCur = estado(cur);

  const confere = conferirTotal(linhas, total);
  const nCartoes = linhas.length + 2;

  const pilulaDoCartao = (k: number): { txt: string; fundo: string; cor: string } => {
    if (k === 0) return { txt: 'Antes da tabela', fundo: '#E2E8F0', cor: '#334155' };
    if (k === nCartoes - 1) return { txt: 'Já vem impresso', fundo: '#E2E8F0', cor: '#334155' };
    const s = sitDe(linhas[k - 1][0]);
    if (!s) return { txt: 'Situação não informada', fundo: '#E2E8F0', cor: '#334155' };
    if (s.fechada) return { txt: 'Fechada', fundo: COR.verdeFundo, cor: COR.verde };
    if (s.semRG) return { txt: 'Falta cadastrar o RG', fundo: COR.ambarFundo, cor: COR.ambar };
    return { txt: 'Aberta', fundo: COR.azulClaro, cor: '#1E40AF' };
  };

  const valorMudo = (v: string) => v === '' || v === '-' || v === '—';

  return (
    <div
      className="fc-noprint"
      tabIndex={0}
      onKeyDown={aoTeclar}
      onKeyUp={aoSoltarTecla}
      style={{ display: 'flex', flexDirection: 'column', gap: 16, outline: 'none', color: COR.texto }}
    >
      <style>{`
        .cdp-lupa { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 10px; min-width: 640px; }
        .cdp-grupos { display: grid; grid-template-columns: repeat(20, minmax(0, 1fr)); gap: 8px; min-width: 620px; }
        .cdp-ir:focus, .cdp-chk:focus { outline: none; }
        .cdp-chk:focus-visible, .cdp-ir:focus-visible:not([data-cdp-atual]) { outline: 2px solid ${COR.azul}; outline-offset: -2px; border-radius: 10px; }
      `}</style>

      {/* Faixa do topo: o que é, progresso e recomeçar */}
      <div style={{ background: COR.marinho, borderRadius: 14, padding: '18px 24px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px 28px' }}>
        <div style={{ flex: '999 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#A9BCE0' }}>
            Quadro Demonstrativo · Condensado (Amargosa)
          </div>
          <div style={{ fontSize: 21, fontWeight: 700, color: '#FFFFFF' }}>
            Passar para o papel — {municipio} · {nomeCiclo}
          </div>
        </div>
        <div style={{ width: 300, maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#DCE6FB' }}>
            <span style={{ fontWeight: 600 }}>{nCopiados} de {passos.length} blocos copiados</span>
            <span>{pct}</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: '#233B66', overflow: 'hidden' }}>
            <div style={{ height: 8, width: pct, background: '#5EEAD4', borderRadius: 999 }} />
          </div>
        </div>
        <button
          type="button"
          onClick={recomecar}
          style={{ height: 44, padding: '0 16px', borderRadius: 8, border: '1px solid #4B6597', background: 'transparent', color: '#FFFFFF', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Recomeçar
        </button>
      </div>

      {/* Avisos: bloqueio, rascunho e blocos que mudaram */}
      {bloqueios.length > 0 && (
        <div style={{ padding: '12px 16px', borderRadius: 12, background: COR.vermelhoFundo, color: COR.vermelho, fontSize: 14 }}>
          <div style={{ fontWeight: 700 }}>Não copie ainda: o Condensado tem linha bloqueada. Resolva no Painel do Ciclo.</div>
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            {bloqueios.map((b, i) => (
              <li key={i}><b>{b.area}</b> — {b.motivo}</li>
            ))}
          </ul>
        </div>
      )}
      {mudaram.length > 0 && (
        <div style={{ padding: '10px 16px', borderRadius: 12, background: COR.ambarFundo, color: COR.ambar, fontSize: 14 }}>
          <b>{mudaram.length === 1 ? '1 bloco mudou' : `${mudaram.length} blocos mudaram`} depois de copiado:</b>{' '}
          {mudaram.map((p) => `${p.area} · ${p.nome}`).join('; ')}. Confira no papel e marque de novo.
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px', padding: '0 4px' }}>
        {emAndamento.length > 0 ? (
          <>
            <span style={{ ...PILULA, padding: '5px 12px', fontSize: 13, background: COR.ambarFundo, color: COR.ambar }}>Rascunho</span>
            <span style={{ fontSize: 13, color: '#334155' }}>
              {semRG.length > 0 && <><b>Falta cadastrar o RG:</b> {semRG.join(', ')}</>}
              {semRG.length > 0 && abertas.length > 0 && ' · '}
              {abertas.length > 0 && <><b>Aberta:</b> {abertas.join(', ')}</>}
            </span>
          </>
        ) : (
          <span style={{ ...PILULA, padding: '5px 12px', fontSize: 13, background: COR.verdeFundo, color: COR.verde }}>✓ Todas as áreas fechadas</span>
        )}
        <span style={{ flexGrow: 1 }} />
        <span style={{ fontSize: 13, color: COR.cinza }}><b style={{ color: COR.caneta }}>Em vermelho:</b> escreva de caneta vermelha. Situação da área e origem dos positivos ficam na lupa: não vão para o papel.</span>
      </div>

      {/* Lupa fixa: "Você está aqui" */}
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#FFFFFF', border: `2px solid ${COR.azul}`, borderRadius: 14, padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 8px 24px rgba(15, 35, 71, 0.12)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 16px' }}>
          <div style={{ flex: '999 1 380px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: COR.azul }}>{cur.onde}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px', fontSize: 19, fontWeight: 700, color: COR.marinho }}>
              <span>{cur.area}</span>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: cur.cor }} />
              <span style={{ color: cur.cor }}>{cur.nome}</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: COR.cinza }}>{cur.faixaLonga}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 16px', fontSize: 13, color: COR.cinza }}>
            <span style={DICA}><span style={TECLA}>↑</span><span style={TECLA}>↓</span>andar</span>
            <span style={DICA}><span style={{ ...TECLA, padding: '2px 9px' }}>Espaço</span>copiei, avança</span>
            <span style={DICA}><span style={TECLA}>⌫</span>desfazer</span>
            <span>Clique = ir para o bloco</span>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div className="cdp-lupa">
            {cur.celulas.map((c, i) => {
              const ancora = cur.tipo === 'bloco' && c.n === '1';
              const mudo = valorMudo(c.v);
              const longo = !c.texto && c.v.length > 5 && c.lupa === 1;
              return (
                <div key={i} style={{ gridColumn: `span ${c.lupa}`, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 4, padding: '10px 12px', borderRadius: 10, background: ancora ? '#EEF4FF' : '#F8FAFC', border: `1px solid ${ancora ? '#93B4F5' : '#CBD5E1'}` }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minHeight: 28 }}>
                    {c.n !== '' && (
                      <span style={{ flexShrink: 0, padding: '0 5px', borderRadius: 4, background: '#E2E8F0', fontFamily: TECLA.fontFamily, fontSize: 11, lineHeight: '14px', fontWeight: 600, color: '#334155' }}>{c.n}</span>
                    )}
                    <span style={{ fontSize: 11, lineHeight: '14px', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: ancora ? COR.azul : COR.cinza }}>{c.rot}</span>
                  </div>
                  <div style={{ fontSize: c.texto ? 24 : longo ? 26 : 34, lineHeight: '40px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: mudo ? COR.cinzaClaro : c.vermelho ? COR.caneta : ancora ? '#1E3A8A' : COR.texto }}>
                    {c.v === '' ? '—' : c.v}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px', fontSize: 14, color: '#334155' }}>
          <span style={{ padding: '2px 8px', borderRadius: 6, background: '#F1F5F9', color: COR.cinza, fontSize: 12, fontWeight: 600 }}>Só na tela</span>
          <span style={{ flex: '999 1 360px', minWidth: 0 }}>
            {estadoCur === 'mudou' && <b style={{ color: COR.ambar }}>Os números deste bloco mudaram depois que você copiou. </b>}
            {cur.extra}
          </span>
          <span style={{ color: COR.cinza, fontWeight: 600 }}>{proxTxt}</span>
        </div>
      </div>

      {/* A folha: um cartão por linha do papel, cada linha em 4 blocos */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {Array.from({ length: nCartoes }, (_, k) => {
          const meus = passos.filter((p) => p.cartao === k);
          const cop = meus.filter((p) => estado(p) === 'copiado').length;
          const completa = cop === meus.length;
          const pil = pilulaDoCartao(k);
          const ehTotal = k === nCartoes - 1;
          const titulo = k === 0 ? 'Cabeçalho' : ehTotal ? (total[0] || 'TOTAL') : linhas[k - 1][0];
          const sub = k === 0 ? 'Topo do formulário' : ehTotal ? 'Última linha do papel' : `Linha ${k} de ${linhas.length}`;
          return (
            <div key={k} style={{ background: '#FFFFFF', border: `1px solid ${cur.cartao === k ? COR.azul : COR.borda}`, borderRadius: 14, padding: '14px 18px', display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: '12px 16px' }}>
              <div style={{ width: 170, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: COR.cinza }}>{sub}</div>
                <div style={{ fontSize: 18, lineHeight: '22px', fontWeight: 700, color: COR.marinho }}>{titulo}</div>
                <span style={{ ...PILULA, background: pil.fundo, color: pil.cor }}>{pil.txt}</span>
                <span style={{ ...PILULA, background: completa ? COR.verdeFundo : '#F1F5F9', color: completa ? COR.verde : COR.cinza }}>
                  {completa ? '✓ Linha copiada' : `${cop} de ${meus.length} ${meus.length === 1 ? 'bloco' : 'blocos'}`}
                </span>
              </div>
              <div style={{ flex: '999 1 560px', minWidth: 0, overflowX: 'auto' }}>
                <div className="cdp-grupos">
                  {meus.map((p) => {
                    const est = estado(p);
                    const ehCur = p.id === cur.id;
                    const copiado = est === 'copiado';
                    const mudou = est === 'mudou';
                    let fundo = '#F8FAFC';
                    let sombra = 'inset 0 0 0 1px #E2E8F0';
                    let corV: string = COR.texto;
                    let peso = 600;
                    let corRot: string = COR.cinza;
                    let corNome = p.cor;
                    let traco: string = COR.cinzaClaro;
                    if (copiado && !ehCur) { fundo = '#FFFFFF'; corV = COR.cinzaClaro; peso = 500; corRot = COR.cinzaClaro; corNome = COR.cinzaClaro; }
                    if (mudou && !ehCur) { fundo = '#FFFBEB'; sombra = 'inset 0 0 0 1px #D97706'; }
                    if (ehCur) { fundo = COR.azulClaro; sombra = `inset 0 0 0 2px ${COR.azul}`; corV = '#0B1B36'; peso = 700; corRot = '#1E3A8A'; corNome = COR.marinho; traco = '#334155'; }
                    return (
                      <div
                        key={p.id}
                        ref={(el) => { refs.current[p.id] = el; }}
                        aria-current={ehCur ? 'step' : undefined}
                        style={{ gridColumn: p.span, minWidth: 0, display: 'flex', alignItems: 'stretch', borderRadius: 10, background: fundo, boxShadow: sombra }}
                      >
                        <button
                          type="button"
                          className="cdp-chk"
                          aria-label={`${copiado ? 'Desmarcar' : 'Marcar como copiado'}: ${p.area}, ${p.nome}`}
                          onClick={() => (copiado ? desmarcar(p) : marcar(p))}
                          style={{ width: 34, flexShrink: 0, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                        >
                          <span style={{ width: 18, height: 18, boxSizing: 'border-box', borderRadius: 5, border: `1.5px solid ${copiado ? COR.marca : mudou ? '#D97706' : '#94A3B8'}`, background: copiado ? COR.marca : '#FFFFFF', color: copiado ? '#FFFFFF' : '#B45309', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {copiado ? '✓' : mudou ? '!' : ''}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="cdp-ir"
                          data-cdp-ir=""
                          data-cdp-atual={ehCur ? '' : undefined}
                          onClick={() => irPara(p.id, false)}
                          style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6, border: 'none', background: 'transparent', padding: '8px 10px 9px 0', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', fontSize: 14, color: COR.texto }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, lineHeight: '14px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: corNome }}>
                            <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: 2, background: copiado && !ehCur ? '#94A3B8' : p.cor }} />
                            <span>{p.tipo === 'cab' ? 'Cabeçalho' : p.nome}</span>
                            <span style={{ fontWeight: 500, letterSpacing: 0, textTransform: 'none', color: COR.cinza }}>{p.faixa}</span>
                            {mudou && <span style={{ ...PILULA, padding: '0 7px', fontSize: 10, letterSpacing: 0, textTransform: 'none', background: COR.ambarFundo, color: COR.ambar }}>Mudou</span>}
                          </span>
                          <span style={{ display: 'flex', gap: 6 }}>
                            {p.celulas.map((c, i) => (
                              <span key={i} style={{ flexGrow: c.peso, flexBasis: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span style={{ fontSize: 9.5, lineHeight: '11px', minHeight: 22, fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: corRot }}>{c.rot}</span>
                                <span style={{ fontSize: c.texto ? 15 : 16, lineHeight: '22px', fontWeight: peso, fontVariantNumeric: 'tabular-nums', color: valorMudo(c.v) ? traco : c.vermelho ? (copiado && !ehCur ? COR.canetaClara : COR.caneta) : corV }}>{c.v === '' ? '—' : c.v}</span>
                              </span>
                            ))}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Conferência final */}
      <div style={{ background: COR.marinho, borderRadius: 14, padding: '18px 24px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 20px' }}>
        <div style={{ flex: '999 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#A9BCE0' }}>Conferência final · TOTAL do município</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#FFFFFF', fontVariantNumeric: 'tabular-nums' }}>
            {total[5]} existentes · {total[6]} trabalhados · {total[10]} pendentes
          </div>
        </div>
        <span style={{ padding: '8px 14px', borderRadius: 999, fontSize: 14, fontWeight: 600, background: confere === null ? '#E2E8F0' : confere ? COR.verdeFundo : COR.vermelhoFundo, color: confere === null ? '#334155' : confere ? COR.verde : COR.vermelho }}>
          {confere === null ? 'Conferência indisponível' : confere ? `✓ TOTAL confere com a soma das ${linhas.length} áreas` : 'TOTAL diferente da soma das áreas'}
        </span>
      </div>
    </div>
  );
}
